import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, HTMLAttributes, ReactNode } from "react";
import {
    Alert,
    Avatar,
    Button,
    Card,
    Divider,
    Empty,
    Image,
    Input,
    Select,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import type { TableColumnsType, TableProps } from "antd";
import {
    CheckCircleFilled,
    CloseCircleFilled,
    MinusCircleFilled,
    StarFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Link as RouterLink, useNavigate } from "react-router";

import { CustomResult } from "@repo/ui";
import { useMerchantProductsActivity, useMerchants } from "@src/queries";
import type {
    MarketingAdvertisementProductActivity,
    MarketingBonusProductActivity,
    MerchantProductActivityType,
    MerchantProductCityPrice,
} from "@src/types";
import { buildKaspiProductLink, buildProductImageUrl, formatPrice } from "./utils";

const { Title, Text, Link } = Typography;
const { PreviewGroup } = Image;

const DEFAULT_PAGE_SIZE = 50;
type AvailabilityFilterValue = "all" | "available" | "unavailable";
type BrandFilterValue = "all" | "brand" | "non-brand";

const AVAILABILITY_OPTIONS: { label: string; value: AvailabilityFilterValue }[] = [
    { label: "Все товары", value: "all" },
    { label: "В наличии", value: "available" },
    { label: "Нет в наличии", value: "unavailable" },
];

const BRAND_OPTIONS: { label: string; value: BrandFilterValue }[] = [
    { label: "Все бренды", value: "all" },
    { label: "Брендовые", value: "brand" },
    { label: "Без бренда", value: "non-brand" },
];
const MAIN_GROUP_HEADER_CLASS =
    "bg-slate-100 text-[11px] font-semibold uppercase tracking-wide text-slate-700";
const MARKETING_GROUP_HEADER_CLASS =
    "bg-amber-100 text-[11px] font-semibold uppercase tracking-wide text-amber-800";
const MARKETING_SUBGROUP_HEADER_CLASS = "bg-amber-50 text-[11px] font-semibold text-amber-700";
const BONUS_SUBGROUP_HEADER_CLASS = "bg-emerald-50 text-[11px] font-semibold text-emerald-700";
const DISCOUNT_GROUP_HEADER_CLASS =
    "bg-sky-100 text-[11px] font-semibold uppercase tracking-wide text-sky-800";
const CITY_GROUP_HEADER_CLASS = "bg-sky-50 text-[11px] font-semibold text-sky-700";
const MAIN_LEAF_HEADER_CLASS = "bg-white text-[11px] font-semibold text-slate-700";
const MARKETING_LEAF_HEADER_CLASS = "bg-white text-[11px] font-semibold text-amber-700";
const BONUS_LEAF_HEADER_CLASS = "bg-white text-[11px] font-semibold text-emerald-700";
const CITY_LEAF_HEADER_CLASS = "bg-white text-[11px] font-semibold text-slate-600";
const TABLE_ROW_CLASS = "align-center text-[13px] [&>td]:align-center";
const DEFAULT_TABLE_SCROLL_HEIGHT = 520;
const MIN_TABLE_SCROLL_HEIGHT = 240;
const TABLE_BOTTOM_GAP = 48;
const TABLE_SCROLL_EPSILON = 4;

type TableChangeArgs = Parameters<NonNullable<TableProps<MerchantProductActivityType>["onChange"]>>;

interface CityRegistryEntry {
    id: string;
    name: string;
}

const BID_FORMATTER = new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const PERCENT_FORMATTER = new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
});

const buildHeaderCell = (className: string) => (): HTMLAttributes<HTMLElement> => ({ className });

export function MerchantProductsActivityPanel() {
    const merchantsQuery = useMerchants();
    const merchants = merchantsQuery.data?.data ?? [];
    const navigate = useNavigate();

    const [selectedMerchantId, setSelectedMerchantId] = useState<string>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState<string | null>(null);
    const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilterValue>("all");
    const [brandFilter, setBrandFilter] = useState<BrandFilterValue>("all");
    const [tableScrollY, setTableScrollY] = useState(DEFAULT_TABLE_SCROLL_HEIGHT);
    const tableSectionRef = useRef<HTMLDivElement | null>(null);
    const selectedMerchant = useMemo(
        () => merchants.find((merchant) => merchant.id === selectedMerchantId),
        [merchants, selectedMerchantId],
    );

    useEffect(() => {
        if (!merchants.length) {
            setSelectedMerchantId(undefined);
            return;
        }

        setSelectedMerchantId((prev) => {
            if (prev && merchants.some((merchant) => merchant.id === prev)) {
                return prev;
            }
            return merchants[0]?.id;
        });
    }, [merchants]);

    useEffect(() => {
        setPage(1);
    }, [selectedMerchantId, searchTerm, availabilityFilter, brandFilter]);

    const availabilityValue = useMemo(() => {
        if (availabilityFilter === "available") return true;
        if (availabilityFilter === "unavailable") return false;
        return undefined;
    }, [availabilityFilter]);

    const brandValue = useMemo(() => {
        if (brandFilter === "brand") return true;
        if (brandFilter === "non-brand") return false;
        return undefined;
    }, [brandFilter]);

    const productsQuery = useMerchantProductsActivity({
        merchantId: selectedMerchantId,
        page,
        size: pageSize,
        search: searchTerm,
        available: availabilityValue,
        brandFlag: brandValue,
    });

    const { data, isError, error, isFetching, isLoading } = productsQuery;

    const products = data?.data ?? [];
    const total = data?.total ?? 0;

    const handleMerchantChange = useCallback((value: string) => {
        setSelectedMerchantId(value);
    }, []);

    const handleSearchSubmit = useCallback((value: string) => {
        const trimmed = value.trim();
        setSearchInput(value);
        setSearchTerm(trimmed ? trimmed : null);
    }, []);

    const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;
        setSearchInput(nextValue);
        if (!nextValue) {
            setSearchTerm(null);
        }
    }, []);

    const handlePaginationChange = useCallback((...args: TableChangeArgs) => {
        const [pagination] = args;
        if (pagination?.current) {
            setPage(pagination.current);
        }
        if (pagination?.pageSize) {
            setPageSize(pagination.pageSize);
        }
    }, []);

    const merchantOptions = useMemo(
        () =>
            merchants.map((merchant) => ({
                label: merchant.store_name || merchant.merchant_uid,
                value: merchant.id,
            })),
        [merchants],
    );
    const handleGoToProfile = useCallback(() => {
        navigate({
            pathname: "/profile",
            search: selectedMerchantId ? `?merchantId=${selectedMerchantId}` : "",
        });
    }, [navigate, selectedMerchantId]);
    const marketingAlertVisible = Boolean(selectedMerchant && !selectedMerchant.marketing_merchant);

    const cityRegistry = useMemo<CityRegistryEntry[]>(() => {
        const map = new Map<string, string>();
        for (const product of products) {
            for (const cityPrice of product.city_prices ?? []) {
                const key = getCityKey(cityPrice);
                if (!map.has(key)) {
                    map.set(key, cityPrice.city?.name ?? "Город");
                }
            }
        }

        return Array.from(map.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name, "ru", { sensitivity: "base" }));
    }, [products]);

    const columns = useMemo<TableColumnsType<MerchantProductActivityType>>(() => {
        const cityColumns: TableColumnsType<MerchantProductActivityType> = cityRegistry.map(
            (city) => ({
                title: city.name,
                key: `city-${city.id}`,
                onHeaderCell: buildHeaderCell(CITY_GROUP_HEADER_CLASS),
                children: [
                    {
                        title: "Цена",
                        key: `city-${city.id}-price`,
                        width: 50,
                        onHeaderCell: buildHeaderCell(CITY_LEAF_HEADER_CLASS),
                        render: (_value, record) => {
                            const cityPrice = findCityPrice(record, city.id);
                            return renderCityPrice(cityPrice);
                        },
                    },
                    {
                        title: "Размер скидки",
                        key: `city-${city.id}-discount`,
                        width: 45,
                        onHeaderCell: buildHeaderCell(CITY_LEAF_HEADER_CLASS),
                        render: (_value, record) => {
                            const cityPrice = findCityPrice(record, city.id);
                            const percent = calculateDiscountPercent(cityPrice);
                            if (!percent) {
                                return "—";
                            }
                            return `-${PERCENT_FORMATTER.format(percent)}%`;
                        },
                    },
                    {
                        title: "Дата начала",
                        key: `city-${city.id}-start`,
                        width: 45,
                        onHeaderCell: buildHeaderCell(CITY_LEAF_HEADER_CLASS),
                        render: (_value, record) => {
                            const cityPrice = findCityPrice(record, city.id);
                            return formatDate(cityPrice?.discount_suitable_date);
                        },
                    },
                    {
                        title: "Дата окончания",
                        key: `city-${city.id}-end`,
                        width: 45,
                        onHeaderCell: buildHeaderCell(CITY_LEAF_HEADER_CLASS),
                        render: (_value, record) => {
                            const cityPrice = findCityPrice(record, city.id);
                            return formatDate(cityPrice?.discount_exp_date);
                        },
                    },
                    {
                        title: "Статус",
                        key: `city-${city.id}-status`,
                        width: 40,
                        onHeaderCell: buildHeaderCell(CITY_LEAF_HEADER_CLASS),
                        render: (_value, record) => {
                            const cityPrice = findCityPrice(record, city.id);
                            const status = getDiscountStatus(cityPrice);
                            return <Tag color={status.color}>{status.label}</Tag>;
                        },
                    },
                ],
            }),
        );

        return [
            {
                title: "Основная информация",
                onHeaderCell: buildHeaderCell(MAIN_GROUP_HEADER_CLASS),
                children: [
                    {
                        title: "Товар",
                        key: "product",
                        width: 80,
                        fixed: "left",
                        onHeaderCell: buildHeaderCell(MAIN_LEAF_HEADER_CLASS),
                        render: (_value, record) => renderProductCell(record),
                    },
                    {
                        title: "Статус",
                        dataIndex: "available",
                        key: "available",
                        width: 26,
                        fixed: "left",
                        onHeaderCell: buildHeaderCell(MAIN_LEAF_HEADER_CLASS),
                        render: (value: boolean) => (
                            <Tooltip title={value ? "В наличии" : "Нет в наличии"}>
                                {value ? (
                                    <CheckCircleFilled className="text-lg text-green-500" />
                                ) : (
                                    <CloseCircleFilled className="text-lg text-red-400" />
                                )}
                            </Tooltip>
                        ),
                    },
                    {
                        title: "Бренд",
                        key: "brand",
                        width: 22,
                        fixed: "left",
                        onHeaderCell: buildHeaderCell(MAIN_LEAF_HEADER_CLASS),
                        render: (_value, record) => {
                            if (!record.brand) {
                                return (
                                    <Tooltip title="Бренд не указан">
                                        <MinusCircleFilled className="text-lg text-gray-300" />
                                    </Tooltip>
                                );
                            }

                            return (
                                <Tooltip title={record.brand}>
                                    <StarFilled className="text-lg text-amber-500" />
                                </Tooltip>
                            );
                        },
                    },
                    {
                        title: "Себестоимость",
                        dataIndex: "current_cost_price",
                        key: "current_cost_price",
                        width: 50,
                        fixed: "left",
                        onHeaderCell: buildHeaderCell(MAIN_LEAF_HEADER_CLASS),
                        render: (value?: number | null) => formatPrice(value),
                    },
                ],
            },
            {
                title: "Маркетинг",
                onHeaderCell: buildHeaderCell(MARKETING_GROUP_HEADER_CLASS),
                children: [
                    {
                        title: "Реклама",
                        onHeaderCell: buildHeaderCell(MARKETING_SUBGROUP_HEADER_CLASS),
                        children: [
                            {
                                title: "Рекламная оценка",
                                key: "ads-score",
                                width: 25,
                                onHeaderCell: buildHeaderCell(MARKETING_LEAF_HEADER_CLASS),
                                render: (_value, record) => {
                                    const score = record.advertisements?.[0]?.score;

                                    if (score === null || score === undefined) {
                                        return "—";
                                    }

                                    const normalized = Math.min(Math.max(score, 0), 10);
                                    const formatted = normalized.toFixed(1);

                                    let colorClass = "text-red-500"; // 0–5
                                    if (normalized >= 7) {
                                        colorClass = "text-green-600"; // 7–10
                                    } else if (normalized >= 5) {
                                        colorClass = "text-orange-500"; // 5–7
                                    }

                                    return (
                                        <span className={`font-semibold ${colorClass}`}>
                                            {formatted}
                                        </span>
                                    );
                                },
                            },
                            {
                                title: "Ставка за клик",
                                key: "ads-bid",
                                width: 23,
                                onHeaderCell: buildHeaderCell(MARKETING_LEAF_HEADER_CLASS),
                                render: (_value, record) => {
                                    const ads = record.advertisements ?? [];
                                    const first = ads[0];
                                    const formattedBid = formatBid(first?.bid);
                                    const tooltipContent = buildMarketingTooltip(
                                        ads,
                                        renderAdvertisementTooltipLine,
                                    );
                                    const className =
                                        ads.length > 1
                                            ? "cursor-help underline decoration-dotted"
                                            : "";

                                    return (
                                        <Tooltip title={tooltipContent} placement="bottomLeft">
                                            <span className={className}>{formattedBid}</span>
                                        </Tooltip>
                                    );
                                },
                            },
                            {
                                title: "Статус",
                                key: "ads-status",
                                width: 50,
                                onHeaderCell: buildHeaderCell(MARKETING_LEAF_HEADER_CLASS),
                                render: (_value, record) => {
                                    const ads = record.advertisements ?? [];
                                    const status = resolveMarketingStatus(ads[0]);
                                    const tooltipContent = buildMarketingTooltip(ads, (item) => (
                                        <Tag color={resolveMarketingStatus(item).color}>
                                            {resolveMarketingStatus(item).label}
                                        </Tag>
                                    ));

                                    const tag = <Tag color={status.color}>{status.label}</Tag>;

                                    if (!tooltipContent) {
                                        return tag;
                                    }

                                    return (
                                        <Tooltip title={tooltipContent} placement="bottomLeft">
                                            {tag}
                                        </Tooltip>
                                    );
                                },
                            },
                        ],
                    },
                    {
                        title: "Бонус",
                        onHeaderCell: buildHeaderCell(BONUS_SUBGROUP_HEADER_CLASS),
                        children: [
                            {
                                title: "Размер бонуса",
                                key: "bonus-percent",
                                width: 30,
                                onHeaderCell: buildHeaderCell(BONUS_LEAF_HEADER_CLASS),
                                render: (_value, record) => {
                                    const bonuses = record.bonuses ?? [];
                                    const first = bonuses[0];
                                    const formatted = formatBonus(first?.bonus_percent);
                                    const tooltipContent = buildMarketingTooltip(
                                        bonuses,
                                        renderBonusTooltipLine,
                                    );
                                    const className =
                                        bonuses.length > 1
                                            ? "cursor-help underline decoration-dotted"
                                            : "";

                                    return (
                                        <Tooltip title={tooltipContent} placement="bottomLeft">
                                            <span className={className}>{formatted}</span>
                                        </Tooltip>
                                    );
                                },
                            },
                            {
                                title: "Статус",
                                key: "bonus-status",
                                width: 50,
                                onHeaderCell: buildHeaderCell(BONUS_LEAF_HEADER_CLASS),
                                render: (_value, record) => {
                                    const bonuses = record.bonuses ?? [];
                                    const status = resolveMarketingStatus(bonuses[0]);
                                    const tooltipContent = buildMarketingTooltip(
                                        bonuses,
                                        (item) => (
                                            <Tag color={resolveMarketingStatus(item).color}>
                                                {resolveMarketingStatus(item).label}
                                            </Tag>
                                        ),
                                    );

                                    const tag = <Tag color={status.color}>{status.label}</Tag>;
                                    if (!tooltipContent) {
                                        return tag;
                                    }

                                    return (
                                        <Tooltip title={tooltipContent} placement="bottomLeft">
                                            {tag}
                                        </Tooltip>
                                    );
                                },
                            },
                        ],
                    },
                ],
            },
            ...(cityColumns.length
                ? [
                      {
                          title: "Скидка",
                          onHeaderCell: buildHeaderCell(DISCOUNT_GROUP_HEADER_CLASS),
                          children: cityColumns,
                      },
                  ]
                : []),
        ];
    }, [cityRegistry]);

    const scrollX = useMemo(() => 1200 + cityRegistry.length * 520, [cityRegistry.length]);
    const showEmptyState = !merchantsQuery.isLoading && merchants.length === 0;

    const updateTableScrollHeight = useCallback(() => {
        if (typeof window === "undefined" || !tableSectionRef.current) {
            return;
        }

        const containerRect = tableSectionRef.current.getBoundingClientRect();
        const availableHeight = window.innerHeight - containerRect.top - TABLE_BOTTOM_GAP;
        if (availableHeight <= 0) {
            return;
        }

        const headerElement = tableSectionRef.current.querySelector(".ant-table-thead");
        const paginationElement = tableSectionRef.current.querySelector(".ant-table-pagination");

        const headerHeight =
            headerElement instanceof HTMLElement ? headerElement.getBoundingClientRect().height : 0;
        const paginationHeight =
            paginationElement instanceof HTMLElement
                ? paginationElement.getBoundingClientRect().height
                : 0;

        const nextHeight = Math.max(
            availableHeight - headerHeight - paginationHeight,
            MIN_TABLE_SCROLL_HEIGHT,
        );

        setTableScrollY((current) =>
            Math.abs(current - nextHeight) > TABLE_SCROLL_EPSILON ? nextHeight : current,
        );
    }, []);

    useEffect(() => {
        updateTableScrollHeight();
    }, [
        updateTableScrollHeight,
        availabilityFilter,
        brandFilter,
        isError,
        isFetching,
        isLoading,
        merchants.length,
        page,
        pageSize,
        products.length,
        searchTerm,
        selectedMerchantId,
        showEmptyState,
        marketingAlertVisible,
    ]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const handleResize = () => {
            updateTableScrollHeight();
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [updateTableScrollHeight]);

    if (merchantsQuery.isError) {
        return (
            <CustomResult
                type="error"
                title="Не удалось загрузить магазины"
                axiosError={merchantsQuery.error}
                withCard
            />
        );
    }

    return (
        <Card
            title={
                <div className="flex flex-col">
                    <Title level={4} className="!m-0">
                        Панель активности
                    </Title>
                    <Text type="secondary" className="text-sm text-gray-500">
                        Анализируйте маркетинг и скидки по SKU в одном месте
                    </Text>
                </div>
            }
            className="h-full w-full border border-gray-100 shadow-lg"
        >
            {showEmptyState ? (
                <Empty description="Добавьте хотя бы один магазин, чтобы увидеть товары" />
            ) : (
                <div className="flex flex-1 min-h-0 flex-col gap-4">
                    {marketingAlertVisible ? (
                        <Alert
                            type="warning"
                            showIcon
                            message="Маркетинговые данные недоступны"
                            description={
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <span className="text-sm text-gray-700">
                                        Для выбранного магазина нет подключенного маркетингового
                                        аккаунта. Зарегистрируйте его на странице профиля, чтобы
                                        получать данные по рекламе и бонусам.
                                    </span>
                                    <Button type="primary" size="small" onClick={handleGoToProfile}>
                                        Перейти в профиль
                                    </Button>
                                </div>
                            }
                            className="w-full"
                        />
                    ) : null}
                    <div className="flex flex-wrap gap-3">
                        <Input.Search
                            placeholder="Поиск по SKU или названию"
                            allowClear
                            value={searchInput}
                            onChange={handleSearchChange}
                            onSearch={handleSearchSubmit}
                            className="w-full md:max-w-xs"
                        />
                        <Select
                            placeholder="Выберите магазин"
                            options={merchantOptions}
                            value={selectedMerchantId}
                            onChange={handleMerchantChange}
                            loading={merchantsQuery.isFetching && !merchants.length}
                            className="min-w-[240px]"
                        />
                        <Select<AvailabilityFilterValue>
                            value={availabilityFilter}
                            options={AVAILABILITY_OPTIONS}
                            onChange={setAvailabilityFilter}
                            className="min-w-[180px]"
                        />
                        <Select<BrandFilterValue>
                            value={brandFilter}
                            options={BRAND_OPTIONS}
                            onChange={setBrandFilter}
                            className="min-w-[180px]"
                        />
                    </div>

                    <div ref={tableSectionRef} className="flex-1 min-h-0">
                        {isError ? (
                            <CustomResult
                                type="error"
                                title="Не удалось загрузить активность"
                                axiosError={error}
                            />
                        ) : (
                            <Table<MerchantProductActivityType>
                                rowKey={(record) => record.id}
                                columns={columns}
                                dataSource={products}
                                loading={isLoading || isFetching}
                                pagination={{
                                    current: page,
                                    pageSize,
                                    total,
                                    showSizeChanger: true,
                                    pageSizeOptions: [50, 100],
                                }}
                                scroll={{ x: scrollX, y: tableScrollY }}
                                sticky={{ offsetHeader: 0 }}
                                size="small"
                                rowClassName={() => TABLE_ROW_CLASS}
                                className="rounded-lg border border-gray-50"
                                onChange={handlePaginationChange}
                                locale={{
                                    emptyText: <Empty description="Данных нет" />,
                                }}
                            />
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
}

function renderProductCell(record: MerchantProductActivityType) {
    const title = record.title || record.product?.title || "Без названия";
    const link = buildKaspiProductLink(record.product?.shop_link);
    const images = (record.product?.images ?? [])
        .map(buildProductImageUrl)
        .filter((src): src is string => Boolean(src));
    const imageSrc = images[0];

    return (
        <div className="flex items-center gap-3">
            {imageSrc ? (
                <PreviewGroup items={images}>
                    <Image
                        src={imageSrc}
                        width={35}
                        height={35}
                        className="rounded-md object-cover"
                        style={{ objectFit: "cover" }}
                        preview={{ mask: <span className="text-xs">Открыть</span> }}
                    />
                </PreviewGroup>
            ) : (
                <Avatar shape="square" size={48} className="bg-gray-100 text-sm font-medium">
                    {title?.charAt(0)?.toUpperCase() ?? "?"}
                </Avatar>
            )}
            <div className="min-w-0 flex-1">
                {/*<RouterLink*/}
                {/*    to={`/products/${record.id}`}*/}
                {/*    className="block truncate text-sm font-semibold text-[#1677ff]"*/}
                {/*>*/}
                {/*    {title}*/}
                {/*</RouterLink>*/}
                <Tooltip
                    placement="bottomLeft"
                    color="#FAFAFA"
                    title={
                        <div className="flex flex-col gap-1">
                            <RouterLink
                                to={`/products/${record.id}`}
                                className="block truncate text-sm font-semibold text-[#1677ff]"
                            >
                                {record.sku}
                            </RouterLink>
                            <Divider size="small" />
                            {link ? (
                                <Link
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs"
                                >
                                    {title}
                                </Link>
                            ) : (
                                <span className="text-xs font-semibold ">{title}</span>
                            )}
                        </div>
                    }
                >
                    <Text ellipsis>{record.sku}</Text>
                    {/*<span className="cursor-help text-xs  underline decoration-dotted">*/}
                    {/*    {record.sku}*/}
                    {/*</span>*/}
                </Tooltip>
            </div>
        </div>
    );
}

function getCityKey(price: MerchantProductCityPrice) {
    return price.city?.id ?? price.city?.name ?? price.id;
}

function findCityPrice(record: MerchantProductActivityType, cityId: string) {
    return record.city_prices?.find((price) => getCityKey(price) === cityId);
}

function renderCityPrice(cityPrice?: MerchantProductCityPrice) {
    if (!cityPrice) {
        return "—";
    }

    if (
        !cityPrice.old_price ||
        cityPrice.old_price === 0 ||
        cityPrice.price >= cityPrice.old_price
    ) {
        return <span className="text-sm font-medium">{formatPrice(cityPrice.price)}</span>;
    }

    return (
        <div className="flex flex-col text-xs">
            <span className="text-gray-400 line-through">{formatPrice(cityPrice.old_price)}</span>
            <span className="font-semibold text-gray-900">{formatPrice(cityPrice.price)}</span>
        </div>
    );
}

function calculateDiscountPercent(cityPrice?: MerchantProductCityPrice) {
    if (!cityPrice) {
        return null;
    }

    if (!cityPrice.old_price || cityPrice.old_price === 0) {
        return null;
    }

    const diff = ((cityPrice.old_price - cityPrice.price) / cityPrice.old_price) * 100;
    if (!Number.isFinite(diff) || diff <= 0) {
        return null;
    }

    return diff;
}

function formatDate(value?: string | null) {
    if (!value) {
        return "—";
    }

    const parsed = dayjs(value);
    if (!parsed.isValid()) {
        return "—";
    }

    return parsed.format("DD.MM.YYYY");
}

function getDiscountStatus(price?: MerchantProductCityPrice) {
    if (!price) {
        return { label: "Нет", color: "default" as const };
    }

    if (price.discount_available) {
        return { label: "Можно", color: "green" as const };
    }

    if (price.discount_suitable_date) {
        const start = dayjs(price.discount_suitable_date);
        if (start.isValid()) {
            const now = dayjs();
            const diffDays = start.diff(now, "day");
            if (diffDays <= 7 && diffDays >= 0) {
                return { label: "Скоро", color: "gold" as const };
            }
        }
    }

    return { label: "Нет", color: "default" as const };
}

function formatBid(value?: number | null) {
    if (value === null || value === undefined) {
        return "—";
    }
    return `${BID_FORMATTER.format(value)} ₸`;
}

function formatBonus(value?: number | null) {
    if (value === null || value === undefined) {
        return "—";
    }
    return `${PERCENT_FORMATTER.format(value)}%`;
}

function resolveMarketingStatus(item?: MarketingAdvertisementProductActivity) {
    if (!item) {
        return { label: "-", color: "default" as const };
    }
    const productState = item.product_state?.toLowerCase();
    const campaignState = item.campaign?.state?.toLowerCase();

    if (campaignState === "enabled") {
        return mapProductState(productState);
    }
    return mapCampaignState(campaignState);
}

function mapCampaignState(state?: string | null) {
    switch (state) {
        case "enabled":
            return { label: "Включена", color: "green" as const };
        case "paused":
            return { label: "На паузе", color: "orange" as const };
        case "ended":
            return { label: "Завершена", color: "default" as const };
        case "archived":
            return { label: "В архиве", color: "default" as const };
        default:
            return { label: "Неизвестно", color: "default" as const };
    }
}

function mapProductState(state?: string | null) {
    switch (state) {
        case "active":
            return { label: "Активен", color: "green" as const };
        case "enabled":
            return { label: "Включен", color: "green" as const };
        case "paused":
            return { label: "На паузе", color: "orange" as const };
        case "outofstock":
        case "out_of_stock":
            return { label: "Нет в наличии", color: "red" as const };
        default:
            return { label: "Неизвестно", color: "default" as const };
    }
}

function buildMarketingTooltip<
    T extends MarketingAdvertisementProductActivity | MarketingBonusProductActivity,
>(items: T[], renderLine: (item: T) => ReactNode) {
    if (items.length < 1) {
        return null;
    }

    return (
        <div className="flex max-w-[280px] flex-col gap-2">
            {items.map((item) => {
                const link = item.marketing_url;
                return (
                    <div
                        key={`${item.id}-${item.sku}`}
                        className="rounded border border-gray-100 bg-gray-50 p-2"
                    >
                        {link ? (
                            <Link
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs"
                            >
                                {item.campaign.name}
                            </Link>
                        ) : (
                            <div className="text-xs font-semibold text-gray-800">
                                {item.campaign.name}
                            </div>
                        )}
                        <div className="text-xs text-gray-600">{renderLine(item)}</div>
                    </div>
                );
            })}
        </div>
    );
}

function renderAdvertisementTooltipLine(item: MarketingAdvertisementProductActivity) {
    const status = resolveMarketingStatus(item);
    return (
        <div className="flex flex-col">
            <span>{formatBid(item.bid)}</span>
            <Tag color={status.color}>{status.label}</Tag>
        </div>
    );
}

function renderBonusTooltipLine(item: MarketingBonusProductActivity) {
    const status = resolveMarketingStatus(item);
    return (
        <div className="flex flex-col">
            <span>{formatBonus(item.bonus_percent)}</span>
            <Tag color={status.color}>{status.label}</Tag>
        </div>
    );
}
